import { useEffect, useRef, type ReactNode } from 'react';
import { ProductThumb } from './ProductThumb';
import { OrganicLeaf } from './OrganicLeaf';

/**
 * One product, as a tile.
 *
 * The list and the add screen both answer the same question — *which of these
 * things do I mean* — and a person answers it by recognising a package, not by
 * reading a name. A 40px thumbnail on a row is too small to recognise anything;
 * at three columns the same picture is nearly three times the size and twelve
 * of them fit where eight rows did.
 *
 * What is deliberately **not** here is the price. A price answers "which of
 * these should I buy", which is the comparison screen's question; by the time a
 * product is on the list it has already been chosen, and the number was
 * competing with the name for the same glance. It lives in the item's detail
 * sheet now, next to the link that opens the full comparison.
 *
 * ## Two gestures, one of them hidden
 *
 * Tap does the frequent thing — on the list, that is *bought*. **Press and
 * hold** opens the item's sheet, which used to be a `⋯` button in the corner of
 * every single tile. A tile is 102px of picture and the dots sat on top of it,
 * on every tile, for an action taken once a week; the picture is the entire
 * reason the list became a grid, so the control that obscured it lost.
 *
 * The corner button is still **rendered**, and that is not a leftover. Press
 * and hold is a touch gesture and there is no keyboard equivalent, so removing
 * the button would take the quantity, the shop, the note and *removing an item*
 * away from anybody not using their thumb. It is transparent and inert until it
 * is focused, at which point it appears — visible to the people who need it,
 * gone for the people who do not.
 *
 * The corner control is a sibling of the tile button rather than a child:
 * nesting one button inside another is invalid, and browsers resolve it by
 * dropping one of the two — usually the one you wanted.
 */

/**
 * Long enough not to fire while a thumb is starting a scroll, short enough that
 * somebody holding a tile on purpose does not conclude nothing is happening.
 */
const LONG_PRESS_MS = 450;

/** Past this the finger is scrolling the list, not pressing a tile. */
const SLOP_PX = 10;

export interface TileProps {
  /** Drawn large; also the lettered fallback and its colour. */
  name: string;
  /** The second line — size, shop, note, or why this is being suggested. */
  sub?: string | null;
  imagePath?: string | null;
  /** A short overlay on the picture: a quantity, or why the tile is inert. */
  badge?: string | null;
  /**
   * Whether this is an organic (*biologisch*) product. Draws the leaf in the
   * corner, and — because the corner is decorative and the tile's `aria-label`
   * overrides its children — also appends "biologisch" to the announced name,
   * so the marker is not silent to a screen reader.
   */
  organic?: boolean;
  /** Accessible name for the tile itself. Never inferred from the children. */
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  /**
   * Press and hold. The same action is reachable as the corner button, which
   * is what `corner` describes — pass both, or neither.
   */
  onLongPress?: () => void;
  /** The keyboard's way to the long-press action, transparent until focused. */
  corner?: { label: string; glyph: ReactNode; onClick: () => void };
}

export function Tile({
  name,
  sub,
  imagePath = null,
  badge,
  organic = false,
  label,
  disabled = false,
  onClick,
  onLongPress,
  corner,
}: TileProps) {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  /**
   * Whether the hold already did its job. A long press on a mouse still ends in
   * a `click`, and on the list that click means *bought* — so the press would
   * open the sheet and tick the item off behind it. It is reset when the next
   * press begins rather than when the click arrives, because on iOS a
   * long-pressed element often produces no click at all and a flag left set
   * would swallow the following tap.
   */
  const consumed = useRef(false);

  function cancel() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }

  // A tile can be unmounted mid-press — ticking one off removes it from the
  // list — and a timer that outlives its component fires into nothing.
  useEffect(() => cancel, []);

  function start(event: React.PointerEvent) {
    if (!onLongPress || disabled) return;
    consumed.current = false;
    origin.current = { x: event.clientX, y: event.clientY };
    timer.current = window.setTimeout(() => {
      timer.current = null;
      consumed.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  }

  function move(event: React.PointerEvent) {
    if (timer.current === null || !origin.current) return;
    const dx = event.clientX - origin.current.x;
    const dy = event.clientY - origin.current.y;
    if (Math.hypot(dx, dy) > SLOP_PX) cancel();
  }

  return (
    <div className="tile-wrap">
      <button
        className="tile"
        aria-label={organic ? `${label}, biologisch` : label}
        disabled={disabled}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onPointerCancel={cancel}
        // Otherwise a hold on iOS raises the text-selection callout over the
        // sheet that just opened, and a hold on Android opens a context menu.
        onContextMenu={(event) => {
          if (onLongPress) event.preventDefault();
        }}
        onClick={() => {
          if (consumed.current) return;
          onClick?.();
        }}
      >
        <span className="tile-art">
          <ProductThumb src={imagePath} name={name} size="tile" />
          {organic && <OrganicLeaf />}
          {badge && (
            <span className="tile-badge" aria-hidden="true">
              {badge}
            </span>
          )}
        </span>
        <span className="tile-name">{name}</span>
        {sub && <span className="tile-sub">{sub}</span>}
      </button>
      {corner && (
        <button className="tile-more" aria-label={corner.label} onClick={corner.onClick}>
          {corner.glyph}
        </button>
      )}
    </div>
  );
}
